import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  BookOpenIcon,
  FlagIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { useDeleteToolboxPlan } from "../../../api/mutations";
import { useGetTopic, useGetToolboxPlan } from "../../../api/queries";
import Button from "../../../componentLibrary/Button";
import ConfirmationBox from "../../../componentLibrary/ConfirmationBox";
import {
  SectionEmptyState,
  SectionHeader,
  SectionPanel,
} from "../../../componentLibrary/SectionPanel";
import Spinner from "../../../componentLibrary/Spinner";
import { useNotification } from "../../../hooks/useNotification";
import { APIError } from "../../../utils/ErrorHandling";
import {
  topicAccentHex,
  topicCtaGradientStyle,
} from "../../../utils/topicBranding";

// TODO: replace with plan.tools once ToolboxPlanI has a tools field
const DEMO_TOOLS = ["Tool 1", "Tool 2", "Tool 3", "Tool 4"];

export default function ShowPage() {
  const { topicId, planId } = useParams<{ topicId: string; planId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const deleteMutation = useDeleteToolboxPlan({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["toolboxTopicPlans", topicId],
      });
      notify("success", "Plan deleted");
      navigate(`/toolboxes/${topicId}`);
    },
    onError: () => notify("error", "Could not delete plan. Please try again."),
  });

  const {
    data: plan,
    isLoading: planLoading,
    isError: planError,
    error,
  } = useGetToolboxPlan(planId ?? "", { enabled: !!planId });

  const { data: topic } = useGetTopic(topicId ?? "");

  if (!topicId || !planId) {
    return <Navigate to="/error" replace />;
  }

  if (planLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="w-8" />
      </div>
    );
  }

  const backTo = `/toolboxes/${topicId}`;

  if (planError || !plan) {
    const message =
      error instanceof APIError && error.statusCode === 404
        ? "Plan not found."
        : "Could not load plan.";
    return (
      <div className="space-y-4 pt-4">
        <Link
          to={backTo}
          className="flex items-center gap-1.5 text-sm text-electrum-300 hover:text-electrum transition-colors w-fit"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to toolbox
        </Link>
        <p className="text-sm text-gray-500">{message}</p>
      </div>
    );
  }

  const cue = typeof plan.cue === "string" ? plan.cue.trim() : "";
  const timeframe =
    typeof plan.timeframe === "string" ? plan.timeframe.trim() : "";

  return (
    <div className="w-full space-y-6 pt-4">
      <div className="flex items-center justify-between">
        <Link
          to={backTo}
          className="flex items-center gap-1.5 text-sm text-electrum-300 hover:text-electrum transition-colors w-fit"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to toolbox
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-salmon-400 shadow-sm transition-colors hover:bg-red-50"
          >
            <TrashIcon className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>

      <ConfirmationBox
        isOpen={confirmDelete}
        danger
        title="Delete plan?"
        message="This plan and all its data will be permanently deleted."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate({ planId: planId!, topicId: topicId! });
        }}
        onCancel={() => setConfirmDelete(false)}
        onCloseBox={() => setConfirmDelete(false)}
      />

      <div
        className="rounded-xl p-6 text-white"
        style={topicCtaGradientStyle(topic?.color)}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div className="space-y-2">
            {topic && (
              <p className="text-sm font-medium text-white/70 uppercase tracking-wide">
                {topic.title} plan
              </p>
            )}
            <h1 className="text-2xl font-bold leading-snug capitalize">
              {plan.goal || "My plan"}
            </h1>
          </div>
          <Button
            type="button"
            size="lg"
            className="shrink-0 !bg-white hover:!bg-white/90 shadow"
            style={{ color: topicAccentHex(topic?.color) }}
            onClick={() => {}}
          >
            Start Plan
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4 items-start">
        <SectionPanel>
          <SectionHeader
            icon={<FlagIcon className="w-5 h-5 text-electrum-300 shrink-0" />}
            title="Plan details"
            action="Edit"
            actionTo={`/toolboxes/${topicId}/plans/${planId}/edit`}
          />
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-400 mb-0.5">Goal</p>
              <p className="text-sm text-gray-900">
                {plan.goal ? (
                  `To ${plan.goal}`
                ) : (
                  <span className="italic text-gray-400">No goal set</span>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-400 mb-0.5">
                {cue ? "Cue" : timeframe ? "Time" : "When"}
              </p>
              <p className="text-sm text-gray-900">
                {cue ? (
                  `When ${cue}`
                ) : timeframe ? (
                  timeframe
                ) : (
                  <span className="italic text-gray-400">
                    No cue or timeframe set
                  </span>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-400 mb-1">
                I will...
              </p>
              {DEMO_TOOLS.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {DEMO_TOOLS.map((tool) => (
                    <Button
                      key={tool}
                      type="button"
                      size="sm"
                      className="!justify-start shrink px-3 !font-medium"
                    >
                      {tool}
                    </Button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">
                  Tools will be added here.
                </p>
              )}
            </div>
          </div>
        </SectionPanel>

        <SectionPanel className="h-full">
          <SectionHeader
            icon={
              <BookOpenIcon className="w-5 h-5 text-electrum-300 shrink-0" />
            }
            title="My Reflections"
            action="New reflection"
            actionTo="#"
          />
          <SectionEmptyState>
            <p className="text-sm text-gray-400 py-2 mb-3">
              Reflections you add after working on this plan will appear here.
            </p>
            <span className="inline-flex items-center gap-1.5 rounded-md bg-electrum-100 px-3 py-1.5 text-sm font-bold text-electrum shadow opacity-50 cursor-not-allowed">
              <PlusIcon className="h-4 w-4" />
              New reflection
            </span>
          </SectionEmptyState>
        </SectionPanel>
      </div>
    </div>
  );
}
